import { useEffect, useState } from "react";
import { Activity, RefreshCw } from "lucide-react";
type Mode = "live" | "simulation";
type Event = {
  id: string;
  baseline_id: string;
  detected_at: string;
  risk: string;
  correlated: boolean;
  explanation: string;
  status: string;
  incomplete_evidence: boolean;
  changes: Array<{
    signalType: string;
    previousValue: unknown;
    currentValue: unknown;
    firstObservedAt: string;
    evidence: { observationId: string; source: string };
    expectedChangeId?: string;
  }>;
};
type View = {
  mode: Mode;
  simulationEnabled: boolean;
  canManage: boolean;
  canObserve: boolean;
  baselines: Array<{
    id: string;
    version: number;
    established_at: string;
    superseded_at: string | null;
  }>;
  events: Event[];
  jobs: Array<{
    id: string;
    status: string;
    action: string;
    failure_code: string | null;
    created_at: string;
  }>;
  observations: Array<{
    id: string;
    signal_type: string;
    normalized_value: unknown;
    status: string;
    failure_code: string | null;
    source: string;
    collection_method: string;
    observed_at: string;
  }>;
  expectedChanges: Array<{
    id: string;
    reason: string;
    ends_at: string;
    signal_types: string[];
  }>;
};
type Api = <T>(path: string, init?: RequestInit) => Promise<T>;
const scenarios = [
  ["unchanged", "No meaningful change"],
  ["dns", "DNS change"],
  ["certificate", "Certificate renewal"],
  ["hosting", "Hosting / ASN change"],
  ["redirect", "Redirect introduced"],
  ["content", "Content change"],
  ["resource", "New resource host"],
  ["correlated", "Multiple correlated changes"],
  ["approved", "Approved change"],
  ["failure", "Collector failure"],
];
const date = (v: string) => new Date(v).toLocaleString();
const value = (v: unknown) => JSON.stringify(v);
const lacksConclusion = (event: Event) =>
  event.risk === "NORMAL" && event.incomplete_evidence;
const riskLabel = (event: Event) =>
  lacksConclusion(event) ? "INCOMPLETE EVIDENCE" : event.risk;
export function TrustDriftView({
  targetId,
  verified,
  api,
}: {
  targetId: string | undefined;
  verified: boolean;
  api: Api;
}) {
  const [mode, setMode] = useState<Mode>("live"),
    [view, setView] = useState<View>(),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0),
    [scenario, setScenario] = useState("unchanged"),
    [accept, setAccept] = useState(false);
  const [approvalReason, setApprovalReason] = useState(""),
    [approvalSignals, setApprovalSignals] = useState<string[]>([]);
  const endpoint = targetId ? `/v1/domains/${targetId}/trust-drift` : undefined;
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setView(undefined);
    setError("");
    setAccept(false);
    async function load() {
      if (!endpoint) return;
      try {
        const data = await api<View>(endpoint + "?mode=" + mode);
        if (!active) return;
        setView(data);
        setError("");
        if (data.jobs.some((j) => ["queued", "running"].includes(j.status)))
          timer = setTimeout(() => void load(), 3000);
      } catch {
        if (active)
          setError(
            "Trust Drift is unavailable. Check that the feature is enabled and the database migrations have been applied.",
          );
      }
    }
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [endpoint, api, mode, revision]);
  async function action(name: string, body: unknown) {
    if (!endpoint) return;
    setBusy(true);
    setError("");
    try {
      await api(endpoint + "/" + name + "?mode=" + mode, {
        method: "POST",
        body: JSON.stringify(body),
      });
      setRevision((r) => r + 1);
    } catch (cause) {
      const payload = (cause as { payload?: { error?: string } }).payload;
      setError(
        payload?.error === "observation_cooldown"
          ? "Live collections are limited to one request per minute. Wait until one minute after your last baseline or observation request, then try again."
          : payload?.error?.replaceAll("_", " ") ??
            "The action could not be completed.",
      );
    } finally {
      setBusy(false);
    }
  }
  const baseline = view?.baselines.find((b) => !b.superseded_at);
  const latest = view?.events.find((e) => e.baseline_id === baseline?.id);
  const incomplete = latest?.incomplete_evidence;
  const pending = view?.jobs.some((j) =>
    ["queued", "running"].includes(j.status),
  );
  const disabled = busy || pending || !verified;
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="eyebrow">Proprietary Trustemetry technology</p>
          <h1>Domain Trust Drift</h1>
          <p>
            Compare observable domain changes with an independent historical
            baseline.
          </p>
        </div>
        <button
          className="secondary-button"
          onClick={() => setRevision((r) => r + 1)}
          disabled={busy}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>
      {!targetId && <p>Select a monitored domain to begin.</p>}
      {targetId && !verified && (
        <p role="status">
          Verified ownership and enabled monitoring are required to collect
          observations.
        </p>
      )}
      {error && (
        <p className="demo-notice check-notice error" role="alert">
          {error}
        </p>
      )}
      {view && (
        <>
          <div className="drift-controls">
            <label>
              Evidence mode{" "}
              <select
                aria-label="Evidence mode"
                value={mode}
                disabled={busy}
                onChange={(e) => setMode(e.target.value as Mode)}
              >
                <option value="live">Live observations</option>
                {view.simulationEnabled && (
                  <option value="simulation">Controlled simulation</option>
                )}
              </select>
            </label>
            {mode === "simulation" && (
              <label>
                Scenario{" "}
                <select
                  aria-label="Scenario"
                  value={scenario}
                  onChange={(e) => setScenario(e.target.value)}
                >
                  {scenarios.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {view.canObserve && (
              <button
                className="primary-button"
                disabled={disabled || !baseline}
                onClick={() =>
                  void action("observe", {
                    scenario: mode === "simulation" ? scenario : "unchanged",
                  })
                }
              >
                Run observation
              </button>
            )}
          </div>
          {mode === "simulation" && (
            <p className="demo-notice" role="status">
              Controlled simulation — synthetic evidence, separate from live
              domain risk. No network requests are made.
            </p>
          )}
          <section className="drift-summary-grid">
            <article className="panel drift-risk-card">
              <span>Current Trust Drift Risk</span>
              <strong
                className={
                  "drift-risk " +
                  (latest
                    ? lacksConclusion(latest)
                      ? "incomplete"
                      : latest.risk.toLowerCase()
                    : "")
                }
              >
                <Activity size={22} />
                {latest ? riskLabel(latest) : "NOT COMPARED"}
              </strong>
              <p>
                Last baseline comparison:{" "}
                {latest ? date(latest.detected_at) : "Not yet run"}
              </p>
              {incomplete && (
                <p>
                  Coverage is incomplete. Failed collectors do not establish a
                  normal or adverse security conclusion.
                </p>
              )}
            </article>
            <article className="panel">
              <h2>Independent baseline</h2>
              <p>
                {baseline
                  ? `Version ${baseline.version} · ${date(baseline.established_at)}`
                  : "No baseline established"}
              </p>
              <p>
                A record of observed state; not a URL category or a security
                certification.
              </p>
              {view.canManage && (
                <>
                  <label>
                    <input
                      type="checkbox"
                      checked={accept}
                      onChange={(e) => setAccept(e.target.checked)}
                    />{" "}
                    Accept a new baseline from a fresh observation; retain
                    previous versions.
                  </label>
                  <button
                    className="secondary-button"
                    disabled={disabled || !accept}
                    onClick={() =>
                      void action("baseline", { acceptBaseline: true })
                    }
                  >
                    {baseline
                      ? "Establish new baseline version"
                      : "Establish baseline"}
                  </button>
                </>
              )}
            </article>
            <article className="panel">
              <h2>Latest collection</h2>
              <p>
                {
                  view.observations.filter((o) => o.status === "succeeded")
                    .length
                }{" "}
                / {view.observations.length} signals available
              </p>
              {view.jobs[0] && (
                <p role="status">
                  {view.jobs[0].action}: {view.jobs[0].status}
                  {view.jobs[0].failure_code
                    ? " — " + view.jobs[0].failure_code.replaceAll("_", " ")
                    : ""}
                </p>
              )}
              {pending && (
                <p>
                  Waiting for the monitoring worker. The view refreshes
                  automatically.
                </p>
              )}
            </article>
          </section>
          {view.events.map((event) => (
            <section className="panel drift-event-feature" key={event.id}>
              <div className="panel-header">
                <h2>
                  {event.risk === "NORMAL"
                    ? event.incomplete_evidence
                      ? "Incomplete comparison"
                      : "No material change"
                    : "Trust Drift event"}
                </h2>
                <span
                  className={
                    lacksConclusion(event)
                      ? "status-badge unknown"
                      : event.correlated
                        ? "correlation-badge"
                        : "status-badge"
                  }
                >
                  {event.correlated && !lacksConclusion(event)
                    ? `${event.changes.length} correlated changes`
                    : riskLabel(event)}
                </span>
              </div>
              <p>
                <time>{date(event.detected_at)}</time> · {event.status} · {mode}
              </p>
              <p className="drift-explanation">{event.explanation}</p>
              <div className="drift-change-grid">
                {event.changes.map((c) => (
                  <article key={c.signalType}>
                    <h3>{c.signalType.replaceAll("_", " ")}</h3>
                    <p>First observed: {date(c.firstObservedAt)}</p>
                    <div>
                      Previous: <code>{value(c.previousValue)}</code>
                    </div>
                    <div>
                      Current: <code>{value(c.currentValue)}</code>
                    </div>
                    <small>
                      Source: {c.evidence.source} · Evidence:{" "}
                      {c.evidence.observationId}
                    </small>
                    {c.expectedChangeId && (
                      <p>Matches approved change {c.expectedChangeId}</p>
                    )}
                  </article>
                ))}
              </div>
              {event.status === "open" && view.canManage && (
                <button
                  className="secondary-button"
                  disabled={busy}
                  onClick={() =>
                    void action("acknowledge", { eventId: event.id, notes: "" })
                  }
                >
                  Acknowledge event
                </button>
              )}
            </section>
          ))}
          <section className="panel drift-event-feature">
            <h2>Evidence and source</h2>
            {view.observations.length === 0 ? (
              <p>No observations collected.</p>
            ) : (
              <div className="drift-evidence">
                {view.observations.map((o) => (
                  <details key={o.id}>
                    <summary>
                      {o.signal_type.replaceAll("_", " ")} — {o.status}
                    </summary>
                    <p>
                      {date(o.observed_at)} · {o.source} · {o.collection_method}
                    </p>
                    <pre>
                      {o.status === "failed"
                        ? o.failure_code
                        : value(o.normalized_value)}
                    </pre>
                    <small>Evidence ID: {o.id}</small>
                  </details>
                ))}
              </div>
            )}
          </section>
          <section className="panel drift-event-feature">
            <h2>Historical timeline</h2>
            <ul>
              {view.baselines.map((b) => (
                <li key={b.id}>
                  {date(b.established_at)} — Baseline v{b.version}
                  {b.superseded_at
                    ? " (superseded; evidence retained)"
                    : " (current)"}
                </li>
              ))}
              {view.jobs.map((j) => (
                <li key={j.id}>
                  {date(j.created_at)} — {j.action}: {j.status}
                  {j.failure_code ? " · " + j.failure_code : ""}
                </li>
              ))}
            </ul>
          </section>
          {view.canManage && (
            <section className="panel drift-event-feature">
              <h2>Approve an expected change</h2>
              <p>
                Approve selected signal changes for the next hour. Evidence
                remains in history.
              </p>
              <div className="drift-controls">
                {view.observations.map((o) => (
                  <label key={o.signal_type}>
                    <input
                      type="checkbox"
                      checked={approvalSignals.includes(o.signal_type)}
                      onChange={(e) =>
                        setApprovalSignals((prev) =>
                          e.target.checked
                            ? [...prev, o.signal_type]
                            : prev.filter((s) => s !== o.signal_type),
                        )
                      }
                    />
                    {o.signal_type.replaceAll("_", " ")}
                  </label>
                ))}
              </div>
              <label>
                Reason{" "}
                <input
                  aria-label="Approval reason"
                  maxLength={500}
                  value={approvalReason}
                  onChange={(e) => setApprovalReason(e.target.value)}
                />
              </label>
              <button
                className="secondary-button"
                disabled={
                  disabled ||
                  approvalSignals.length === 0 ||
                  approvalReason.trim().length < 5
                }
                onClick={() =>
                  void action("approve", {
                    signalTypes: approvalSignals,
                    reason: approvalReason,
                    startsAt: new Date().toISOString(),
                    endsAt: new Date(Date.now() + 3600_000).toISOString(),
                  })
                }
              >
                Approve one-hour window
              </button>
              <ul>
                {view.expectedChanges.map((c) => (
                  <li key={c.id}>
                    {c.reason} — through {date(c.ends_at)}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
